import React from 'react';

import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

import useResponsive from '~/hooks/useResponsive';

import useSiteOptions, { SiteOption } from '../hooks/useSiteOptions';

interface AddWebsiteMenuProps {
  isCloudHosting: boolean;
  menuAnchorEl?: HTMLElement | null;
  setMenuAnchorEl?: (el: HTMLElement | null) => void;
  onMenuOptionClick: (option: SiteOption) => void;
}

const AddWebsiteMenu: React.FC<AddWebsiteMenuProps> = ({
  isCloudHosting,
  menuAnchorEl = undefined,
  setMenuAnchorEl = () => {},
  onMenuOptionClick,
}) => {
  const { isMobile } = useResponsive();
  const isMobileView = isMobile();
  const siteOptions = useSiteOptions(isCloudHosting);

  const handleCloseMenu = () => setMenuAnchorEl(null);

  const handleMenuOptionClick = (option: SiteOption) => {
    if (!option.enabled) return;
    onMenuOptionClick(option);
    handleCloseMenu();
  };

  const menuOptions = (() =>
    siteOptions?.map((option, idx) => {
      const { title, enabled, cardLogo, cardIcon, description } = option;
      return (
        <MenuItem
          key={title}
          onClick={() => handleMenuOptionClick(option)}
          disabled={!enabled}
          data-testid={`option-${title}`}
          sx={idx === siteOptions.length - 1 ? undefined : { mb: 1 }}
        >
          {(cardLogo || cardIcon) && (
            <ListItemIcon sx={{ pr: 1.25, color: 'grey.700' }}>
              {cardLogo ? (
                <img src={cardLogo} alt={title} width={32} height={32} />
              ) : (
                cardIcon
              )}
            </ListItemIcon>
          )}
          <ListItemText
            primary={title}
            secondary={description}
            primaryTypographyProps={{
              variant: 'h3',
              color: 'grey.700',
            }}
            secondaryTypographyProps={{
              variant: isMobileView ? 'body2' : 'body1',
              color: 'grey.700',
              pt: 0.5,
            }}
          />
        </MenuItem>
      );
    }))();

  return (
    <Menu
      anchorEl={menuAnchorEl}
      open={!!menuAnchorEl}
      onClose={handleCloseMenu}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          sx: { py: '2px', borderRadius: '0 0 8px 8px' },
        },
      }}
    >
      {menuOptions}
    </Menu>
  );
};

export default AddWebsiteMenu;
